import * as _ from 'lodash';
import {batch} from 'react-redux';
import {protocolExplorerHelper} from 'lib/explorerHelper';
import {ProtocolExplorerAccordionGroup} from 'constants/protocolExplorerEnum';
import {setAllPartOfTipTouchOffTask} from 'lib/PipettingProfileItemHelper';
import {callAPI, cancelAxiosRequest, invokeAPI, resolveIntl, setErrorCodeNumber} from './commonBusiness';
import {changeView, showLoading, hideLoading, showSnackbar} from '../actions/appStateAction';
import {setSelectedTipChannel, setPendingSaveExistStatus} from '../actions/protocolEditorAction';
import {
  batchRestoreCardStateById,
  resetCardState,
  resetGroupState,
  restoreCardState,
  restoreCardStateById,
  restoreCardTaskState,
  restoreGroupState,
} from '../actions/cardStateAction';
import {resetLabwareState, restoreLabwareState} from '../actions/labwareStateAction';
import {
  setLastOpenedProtocol,
  setAvailableProtocols,
  clearTemporaryUnsavedProtocolData,
} from '../actions/protocolAction';
import {
  setProtocolClipboardEventType,
  setProtocolClipboardItems,
  resetProtocolClipboard,
  setProtocolStateRunningProtocol,
  setCurrentProtocolFolder,
} from '../actions/protocolExplorerAction';
import {isProtocolExecutor, saveState} from './stateBusiness';
import {
  SignalRHub,
  ViewMode,
  TaskType,
  SignalREvent,
  ApiRouteMap,
  Feedback,
  TrayType,
  ExplorerContentTypes,
  FastRobotErrorcode,
  SetTrayModalType,
  ClipboardEventType,
  UserRoleLevel,
  ServiceResponseCode,
} from '../../constants/dataEnum';
import jsonCopy from '../../lib/jsonCopy';
import {capitalizeEveryKeysFirstWord, cloneAndMapObject, find} from '../../lib/objectHelper';
import {resetUndoRedoState} from '../actions/undoRedoAction';
import {globalAddUndoItem, globalUndoRedoClearItem} from './undoRedoBusinessAction';
import {resetTemporaryState} from '../actions/temporaryStateAction';
// eslint-disable-next-line import/no-cycle
import {isRoleLevel, isUserOperator} from './userManagementBusiness';
import {
  resetProtocolEditorAndClearOpenedProtocol,
  setProtocolCheckpoint,
  showAutosaveConfirmationModal,
  showNewUnsavedProtocolModal,
  showProtocolTessaraHasBeenDeleted,
} from './commonProtocolManagementBusiness';
import {getCalculatedRequiredTipsSingleCard, checkNormalizationTasks} from './commonTaskListBusiness';
import {refreshLabwareDefinitions} from './labwareDatabaseBusiness';
import {fetchTrayBarcode} from './trayBusiness';
import {showModalSetTrayID} from '../actions/modalSetTrayIDStateAction';
import {showMissingTrayOnCreateProtocolModal, showSameFolderNameDetectedModal} from './commonModalBusiness';
import {getDataProtocolsAndFolders} from '../../lib/protocolHelper';
import {convertCardStateToCsv, generateCardObject} from './protocolStateConversionBusiness';
import {convertCsvToCardState} from './protocolCSVConversionBusiness';
import {PROTOCOL_UPDATE_PROPERTIES} from '../../constants/cardStateEnum';
import {convertOldStateContentFormat} from './protocolMigrationBusiness';
import {getStateContentForOlderVersion} from '../../lib/backwardsCompatibilityHelper';
import {checkIsInstrumentIdle, clearInstrumentProcess} from './instrumentStatusBusiness';
import {
  shareUnshareProtocolAndFolderApi,
  fetchProtocolById,
  sendProtcolsAndFolder,
  getProtocolByIdAndUserId,
  isFolderContainsTessaraProtocolApi,
  getFolderDetailsById,
} from '../../api/protocolManagementApi';
import {showFolderHasTessaraProtocol, showProtocolOrFolderNotAvailable} from './modalStateBusinessAction';
import GlobalReduxStore from '../globalReduxStore';
import {showModalSwitchTrayType} from './trayModalBusiness';

const intl = resolveIntl();
export const NEW_PROTOCOL_ID = '-1';

export const fetchProtocols = (userIdParam) => async (dispatch, getState) => {
  const state = getState();
  const {sessionState} = state;
  const {userId} = sessionState.user;

  const response = await dispatch(
    invokeAPI(SignalRHub.PROTOCOL_MANAGEMENT, SignalREvent.GET_PROTOCOLS, userIdParam || userId, true, false),
  );

  return response.payload;
};

export const fetchProtocolsAndFoldersByFolderId = (folderId) => async (dispatch, getState) => {
  const state = getState();
  const {sessionState} = state;
  const {userId} = sessionState.user;
  const response = await dispatch(
    invokeAPI(
      SignalRHub.PROTOCOL_MANAGEMENT,
      SignalREvent.GET_PROTOCOLS_AND_FOLDERS_BY_FOLDER_ID,
      folderId,
      userId,
      true,
      false,
    ),
  );

  return response.payload;
};

export const formatProtocolsAndFolders =
  (protocolsAndFolders, favoriteIds = []) =>
  () => {
    if (!protocolsAndFolders) {
      return [];
    }

    const {protocols, protocolFolders, protocolFolderLineages} = protocolsAndFolders;

    const formattedItems = [];
    let protocolFolderList = [];

    if (protocols) {
      const formattedProtocols = protocols.map((protocol) => {
        const itemType = ExplorerContentTypes.PROTOCOL;
        const groupView = ProtocolExplorerAccordionGroup.PROTOCOLS;
        const isFavorite = favoriteIds.includes(protocol.id);

        return protocolExplorerHelper.shapeToExplorerItem({...protocol, isFavorite}, itemType, groupView);
      });

      formattedItems.push(...formattedProtocols);
    }

    if (protocolFolders) {
      const formattedFolders = protocolFolders.map((folder) => {
        const itemType = ExplorerContentTypes.FOLDER;
        const groupView = ProtocolExplorerAccordionGroup.FOLDERS;

        return protocolExplorerHelper.shapeToExplorerItem(folder, itemType, groupView);
      });

      formattedItems.push(...formattedFolders);
    }

    if (protocolFolderLineages) {
      protocolFolderList = protocolFolderLineages;
    }

    return [formattedItems, protocolFolderList];
  };

const generateNewProtocolName = () => async (dispatch, getState) => {
  const state = getState();
  const {protocolState} = state;
  const {availableProtocols} = protocolState;

  let newProtocolName = 'New Protocol';
  let nextSequence = 1;

  const protocolNames = availableProtocols
    .filter((e) => e.name?.startsWith(newProtocolName))
    .map((e) => parseInt(e.name?.replace(`${newProtocolName} `, ''), 10))
    .filter((e) => Number.isInteger(e))
    .sort((a, b) => a - b);

  if (protocolNames.length > 0) {
    // eslint-disable-next-line no-restricted-syntax
    for (const i in protocolNames) {
      if (protocolNames[i] === nextSequence) {
        nextSequence += 1;
      }
    }
  }

  newProtocolName = `${newProtocolName} ${nextSequence}`;

  return newProtocolName;
};

const doClearPendingSave = (callback, showAutosaveDisabledToast, clearPendingSave) => async (dispatch, getState) => {
  const {protocolState, protocolEditorState} = getState();
  const {lastOpenedProtocol} = protocolState;
  const {autosave, isPendingSaveExist} = protocolEditorState;
  const newPendingSaveStatus = clearPendingSave ? false : isPendingSaveExist;

  await callback();
  dispatch(setPendingSaveExistStatus(newPendingSaveStatus));

  if (!lastOpenedProtocol && !autosave && showAutosaveDisabledToast) {
    dispatch(showSnackbar(Feedback.WARNING, intl.formatMessage({id: 'protocolEditor.autosaveDisabled.snackbar'})));
  }
};

export const processPendingSave =
  (protocolToOpen, callback, isNewProtocol = false, showAutosaveDisabledToast = true) =>
  async (dispatch, getState) => {
    const {protocolState, protocolEditorState} = getState();
    const {lastProtocolCheckpoint, lastOpenedProtocol} = protocolState;
    const {autosave, isPendingSaveExist} = protocolEditorState;
    const openedProtocol = lastProtocolCheckpoint?.openedProtocol || lastOpenedProtocol;

    const isTessaraSuperUser = dispatch(isRoleLevel(UserRoleLevel.TESSARA_SUPER_USER));

    const isTheSameProtocol = openedProtocol && protocolToOpen && protocolToOpen?.id === openedProtocol?.id;
    const isClearPendingSave = openedProtocol && protocolToOpen ? !isTheSameProtocol : true;
    const isProceedChecking = !autosave && (openedProtocol?.isTessaraProtocol ? isTessaraSuperUser : true);

    const callbackPendingSave = () => callback(isTheSameProtocol);
    const callbackClearPendingSave = async (protocolToReplace) => {
      if (protocolToReplace) {
        await dispatch(replaceNewUnsavedRunningProtocol(openedProtocol?.id, protocolToReplace?.payload));
      }
      await dispatch(doClearPendingSave(callbackPendingSave, showAutosaveDisabledToast, isClearPendingSave));
    };

    if (isProceedChecking) {
      const isIdle = await dispatch(checkIsInstrumentIdle());

      const runningProtocol = await dispatch(getRunningProtocol());
      const {payload} = runningProtocol;

      // prevent user to create new protocol when there's a new and unsaved protocol running
      if (isNewProtocol && payload?.id === '-1' && !isIdle) {
        await dispatch(showNewUnsavedProtocolModal());
        return;
      }

      // case for newly logged in user that haven't opened any protocol yet.
      if ((!protocolToOpen || !openedProtocol) && !isPendingSaveExist) {
        await dispatch(setProtocolCheckpoint(protocolToOpen));
        await dispatch(doClearPendingSave(callbackPendingSave, showAutosaveDisabledToast, isClearPendingSave));
        return;
      }

      // opened different protocol with pending saves
      if ((protocolToOpen?.id !== openedProtocol?.id || isNewProtocol) && isPendingSaveExist) {
        dispatch(
          showAutosaveConfirmationModal(
            protocolToOpen,
            true,
            saveProtocol,
            callbackClearPendingSave,
            callbackClearPendingSave,
          ),
        );

        return;
      }

      dispatch(setProtocolCheckpoint(protocolToOpen, false));
    }

    await dispatch(doClearPendingSave(callbackPendingSave, showAutosaveDisabledToast, isClearPendingSave));
  };

export const newProtocol =
  (forceToCreate = false, isTessaraProtocol = false) =>
  async (dispatch, getState) => {
    const {appState, instrumentState} = getState();
    const {viewMode} = appState;
    const {isSimulationMode, isAutomationMode} = instrumentState;

    const doCreateNewProtocol = async () => {
      const {trayCalibrationState, protocolExplorerState, sessionState} = getState();

      if (isAutomationMode || !isSimulationMode) {
        const trayResponse = await dispatch(fetchTrayBarcode());
        if (!trayResponse || !trayResponse.result || !trayResponse.tray) {
          if (!forceToCreate) {
            await dispatch(setErrorCodeNumber(FastRobotErrorcode.MissingTray));
            dispatch(showMissingTrayOnCreateProtocolModal(() => dispatch(newProtocol(true, isTessaraProtocol))));
            return;
          }
        }

        if (!trayResponse.tray || trayResponse.tray?.id <= 0) {
          if (!forceToCreate) {
            await dispatch(setErrorCodeNumber(FastRobotErrorcode.NO_TRAY_ID_FOUND));
            dispatch(showModalSetTrayID(SetTrayModalType.PROTOCOL_EDITOR, {isTessaraProtocol}));
            return;
          }
        }
      }

      const newProtocolName = await dispatch(generateNewProtocolName());
      const {trayType} = trayCalibrationState;
      const folderId = protocolExplorerState.currentProtocolFolder?.id ?? null;

      let newProtocolData = {
        name: newProtocolName,
        id: '-1',
        trayType,
        isTessaraProtocol,
        folderId,
        owner: sessionState.user.userId,
      };

      const doNewProtocol = async (isTheSameProtocol) => {
        dispatch(refreshLabwareDefinitions());

        if (isTheSameProtocol) {
          newProtocolData = {
            ...newProtocolData,
            name: await dispatch(generateNewProtocolName()),
          };
        }

        dispatch(resetCardState());
        dispatch(resetGroupState());
        dispatch(resetLabwareState());
        dispatch(setLastOpenedProtocol(newProtocolData));
        dispatch(setSelectedTipChannel(''));
        dispatch(resetUndoRedoState());
        dispatch(globalUndoRedoClearItem());
        dispatch(globalAddUndoItem());
        dispatch(resetTemporaryState());
        dispatch(changeView(ViewMode.PROTOCOL_EDITOR));

        return newProtocolName;
      };

      dispatch(processPendingSave(newProtocolData, doNewProtocol, true));
    };
    // handle show modal

    if (!isAutomationMode && isSimulationMode && viewMode !== ViewMode.RUNTIME) {
      dispatch(showModalSwitchTrayType(doCreateNewProtocol));
    } else {
      doCreateNewProtocol();
    }
  };

const syncLabwareState = (labwareState, availableLabwareState) => {
  Object.keys(labwareState).forEach((k) => {
    const availableLabware = find(availableLabwareState, {IDPrefix: labwareState[k].IDPrefix});
    labwareState[k].originalName = availableLabware ? availableLabware.name : '';
    labwareState[k].isRenamed = labwareState[k]?.isRenamed ?? labwareState[k].originalName !== labwareState[k].name;
    labwareState[k].wellDepth = availableLabware ? availableLabware.wellDepth : labwareState[k].wellDepth;
    labwareState[k].thumbnail = availableLabware ? availableLabware.thumbnail : labwareState[k].thumbnail;
  });

  return labwareState;
};

export const isTessaraProtocolStillExists =
  (protocolId, callback, latestProtocolData) => async (dispatch, getState) => {
    if (protocolId === NEW_PROTOCOL_ID) return true;

    const {protocolState} = getState();

    const {lastOpenedProtocol} = protocolState;

    const isTessaraProtocolCurrentlyOpened = lastOpenedProtocol?.id === protocolId;

    const protocol = latestProtocolData || (await fetchProtocolById(protocolId));

    // check if protocol has been deleted by the owner or tessara protocol have been converted to regular protocol
    if (!protocol || !protocol?.isTessaraProtocol) {
      dispatch(showProtocolTessaraHasBeenDeleted(callback, isTessaraProtocolCurrentlyOpened));
      return false;
    }

    return true;
  };

export const checkProtocolAvailability = (protocol, errorCallback) => async (dispatch, getState) => {
  const {user} = getState().sessionState;
  const {id, isTessaraProtocol, owner} = protocol || {};

  if (id === NEW_PROTOCOL_ID || user.userId === owner) return true;

  let isProtocolAvailable = await dispatch(isProtocolExists(protocol, errorCallback));

  if (isProtocolAvailable && isTessaraProtocol) {
    isProtocolAvailable = await dispatch(isTessaraProtocolStillExists(id, errorCallback, protocol?.payload));
  }

  return isProtocolAvailable;
};

const isOpenProtocolFromStates = (protocol) => async (dispatch, getState) => {
  const {cardState, protocolEditorState, sessionState} = getState();
  const {autosave} = protocolEditorState;
  const {user} = sessionState;

  const runningProtocol = await dispatch(getRunningProtocol());

  const isCardStateExists = !_.isEmpty(cardState.cards);
  const isProtocolRunning = runningProtocol?.payload;
  const isOperator = dispatch(isRoleLevel(UserRoleLevel.OPERATOR));
  const isProtocolOwner = protocol?.owner === user?.userId;

  if (!isProtocolOwner) return isProtocolOwner;
  return (!isOperator && !isProtocolRunning && isCardStateExists) || !autosave;
};

export const openProtocol =
  (
    protocol,
    // eslint-disable-next-line default-param-last
    errorCallback = () => {},
    openProtocolFromState,
    isResetTemporaryState = true,
  ) =>
  async (dispatch, getState) => {
    const state = getState();

    const {protocolState, protocolEditorState, sessionState} = state;
    const {autosave} = protocolEditorState;
    const {temporaryUnsavedProtocolData} = protocolState;
    const {user: loggedInUser} = sessionState;
    const {id: protocolId, isTessaraProtocol} = protocol || {};

    const response = await getProtocolByIdAndUserId(protocolId, loggedInUser.userId);
    const isProtocolAvailable = await dispatch(
      checkProtocolAvailability({...protocol, ...response?.payload}, errorCallback),
    );

    if (!isProtocolAvailable) return;

    await dispatch(refreshLabwareDefinitions());

    const doOpenProtocol = async (isSameProtocol) => {
      const {availableLabwareState: updatedAvailableLabware} = getState();
      const {csvContent, stateContent, cardObject} = response?.payload || {};
      let newState = {...state};

      // if there is running protocol and if the autosave is disabled,
      // the protocol state that will be loaded is the one in the temporary unsaved protocol data.
      if (!autosave && temporaryUnsavedProtocolData && isSameProtocol && !isTessaraProtocol) {
        newState = JSON.parse(temporaryUnsavedProtocolData);
      } else {
        const isOpenFromStates =
          openProtocolFromState === false
            ? false
            : openProtocolFromState || (await dispatch(isOpenProtocolFromStates(protocol)));

        if (isSameProtocol && isOpenFromStates) {
          dispatch(changeView(ViewMode.PROTOCOL_EDITOR));
          return;
        }

        if (autosave || csvContent) {
          const isOpenByStateContent = stateContent && _.isEmpty(cardObject?.card);

          newState = isOpenByStateContent
            ? convertOldStateContentFormat(JSON.parse(stateContent))
            : convertCsvToCardState(csvContent, updatedAvailableLabware, cardObject);
        }
      }

      if (temporaryUnsavedProtocolData) dispatch(clearTemporaryUnsavedProtocolData());

      const {cardState, labwareState} = newState;
      const {groups} = cardState;
      let {cards, tasks} = cardState;

      const aspirateTasks = Object.values(tasks).filter((task) => task.type === TaskType.ASPIRATE);
      const isTipTouchOffDirectionExist = Boolean(aspirateTasks[0]?.tipTouchOffHeight);
      const selectedCards = Object.values(cards).filter((card) => card.isSelected);

      // make cards auto expanded when isSelected true on open protocol
      if (selectedCards.length === 1) {
        Object.keys(cards).forEach((k) => {
          if (!cards[k]?.isExpanded && cards[k]?.isSelected) {
            cards[k].isExpanded = true;
          }
        });
      }

      const updatedTasks = isTipTouchOffDirectionExist ? tasks : setAllPartOfTipTouchOffTask(tasks, labwareState);
      const newLabwareState = syncLabwareState(labwareState, updatedAvailableLabware);

      const newCardState = dispatch(checkNormalizationTasks(cards, updatedTasks));
      ({newCards: cards, newTasks: tasks} = newCardState);

      const isNewProtocol = protocol.id === NEW_PROTOCOL_ID;
      const protocolToOpen = isNewProtocol ? protocol : response?.payload;

      batch(() => {
        if (isResetTemporaryState) dispatch(resetTemporaryState());
        dispatch(restoreCardState(cards));
        dispatch(restoreCardTaskState(tasks));
        dispatch(restoreGroupState(groups ?? {}));
        dispatch(restoreLabwareState(newLabwareState));
        dispatch(setLastOpenedProtocol(protocolToOpen));
        dispatch(calculateAndRestoreOpenedProtocolTipCounts());
        dispatch(globalUndoRedoClearItem());
        dispatch(globalAddUndoItem());
        dispatch(changeView(ViewMode.PROTOCOL_EDITOR));
      });
    };

    await dispatch(processPendingSave(protocol, doOpenProtocol));
  };

export const calculateAndRestoreOpenedProtocolTipCounts = () => async (dispatch, getState) => {
  const {cardState} = getState();
  const {cards} = cardState;
  const cardIds = Object.keys(cards);

  dispatch(restoreTipCountForCards(cardIds));
};

export const restoreTipCountForCards =
  (cardIds = []) =>
  async (dispatch, getState) => {
    const {cardState} = getState();
    const {cards} = cardState;

    if (cardIds.length === 0) return;

    const updatedCards = [];

    cardIds.forEach((cardId) => {
      const card = cards[cardId];
      const newRequiredTips = dispatch(getCalculatedRequiredTipsSingleCard(card));
      updatedCards.push({...card, requiredTips: newRequiredTips});
    });

    dispatch(batchRestoreCardStateById(updatedCards));
  };

export const restoreTipCountForSingleCard = (cardId) => async (dispatch, getState) => {
  const {cardState} = getState();
  const {cards} = cardState;
  const card = cards[cardId];
  const newRequiredTips = dispatch(getCalculatedRequiredTipsSingleCard(card));

  dispatch(restoreCardStateById({...card, requiredTips: newRequiredTips}));
};

export const goToProtocolEditor = () => async (dispatch, getState) => {
  const state = getState();
  const {protocolState, protocolEditorState} = state;
  const {autosave} = protocolEditorState;

  const {lastOpenedProtocol, lastProtocolCheckpoint} = protocolState;
  const lastOpenedProtocolId = lastOpenedProtocol?.id;

  const {isExecutor, payload} = await dispatch(isProtocolExecutor(true));
  const {id: runningProtocolId} = payload || {};

  if (
    !lastOpenedProtocolId ||
    (autosave && lastOpenedProtocolId === NEW_PROTOCOL_ID) ||
    (runningProtocolId === NEW_PROTOCOL_ID && isExecutor && lastOpenedProtocolId === runningProtocolId) ||
    (!autosave && lastOpenedProtocol?.name !== lastProtocolCheckpoint?.openedProtocol?.name)
  ) {
    return dispatch(newProtocol());
  }

  const loadingMessage = intl.formatMessage({id: 'explorer.loading.openingProtocol'});

  showLoading(loadingMessage);
  await dispatch(openProtocol(lastOpenedProtocol));
  hideLoading();
  return lastOpenedProtocol;
};

export const openOperatorProtocol = () => async (dispatch) => {
  const protocolFolders = await dispatch(fetchProtocolsAndFoldersByFolderId(null));
  const {protocols} = protocolFolders;
  if (protocols.length === 0) {
    dispatch(resetProtocolEditorAndClearOpenedProtocol());
    dispatch(changeView(ViewMode.PROTOCOL_EDITOR));
  } else {
    await dispatch(openProtocol(protocols[protocols.length - 1]));
  }
};

// check saveRunningProtocol and UpdateProtocol
const getProtocolProperties = (protocol) => async (dispatch, getState) => {
  const {cardState, labwareState, configState} = getState();
  const {name, isTessaraProtocol, folderId, cardObject, isEjectToCaddy} = protocol || {};
  const {version} = configState;

  const CardObject = await generateCardObject(cardState, cardObject);

  return {
    Name: name,
    CsvContent: dispatch(convertCardStateToCsv()),
    StateContent: getStateContentForOlderVersion(cardState, labwareState, version),
    IsTessaraProtocol: Boolean(isTessaraProtocol),
    FolderId: folderId || null,
    CardObject,
    IsEjectToCaddy: isEjectToCaddy,
  };
};

export const openLastProtocol = () => async (dispatch, getState) => {
  const state = getState();
  const {protocolState} = state;
  const {lastOpenedProtocol} = protocolState;

  if (lastOpenedProtocol) {
    await dispatch(openProtocol(lastOpenedProtocol));
  }
};

// propertyMap = { columnName: value, ... }
export const updateProtocolState =
  (
    // eslint-disable-next-line default-param-last
    protocolId = null,
    optionalUpdatedProperties,
  ) =>
  async (dispatch, getState) => {
    const state = getState();
    const {sessionState, protocolState} = state;
    const {lastOpenedProtocol} = protocolState;
    const {userId} = sessionState.user;

    const isUpdatingOpenedProtocol = protocolId && protocolId === lastOpenedProtocol?.id;

    let protocol;
    let propertyMap;

    if (isUpdatingOpenedProtocol) {
      protocol = lastOpenedProtocol;
      propertyMap = await dispatch(getProtocolProperties(lastOpenedProtocol));
    } else {
      protocol = await fetchProtocolById(protocolId);
      propertyMap = cloneAndMapObject({...capitalizeEveryKeysFirstWord(protocol)}, PROTOCOL_UPDATE_PROPERTIES);
    }

    if (optionalUpdatedProperties) {
      propertyMap = {...propertyMap, ...capitalizeEveryKeysFirstWord(optionalUpdatedProperties)};
    }

    const {id, version} = protocol || {};

    const response = await dispatch(
      invokeAPI(SignalRHub.PROTOCOL_MANAGEMENT, SignalREvent.UPDATE_PROTOCOL, id, version, userId, propertyMap),
    );

    const isUpdateProtocolSucceed = response.code === ServiceResponseCode.OK;

    if (!isUpdateProtocolSucceed) {
      console.debug('protocol cannot be saved', response);
      return protocol;
    }

    return response.payload;
  };

export const saveProtocol =
  (forceSave = false, setPendingSave = true) =>
  async (dispatch, getState) => {
    const state = getState();
    const {cardState, labwareState, protocolState, sessionState, appState, protocolEditorState, trayCalibrationState} =
      state;
    const {userId} = sessionState.user;
    const {availableProtocols, lastOpenedProtocol} = protocolState;
    const {viewMode, prevViewMode} = appState;
    const isOperator = dispatch(isUserOperator());
    const {trayType} = trayCalibrationState;

    let response = null;
    if (viewMode === ViewMode.RUNTIME && prevViewMode === ViewMode.PROTOCOL_EDITOR && !forceSave) {
      return null; // handled in runtime business to avoid saving wrong protocol
    }

    if ((viewMode === ViewMode.PROTOCOL_EDITOR || viewMode === ViewMode.RUNTIME) && setPendingSave) {
      dispatch(setPendingSaveExistStatus(true));
    }

    // prevent protocol updates when the autosave is turned off
    // and if there's changes by regular tessara user on tessara protocol

    const isTessaraRegularUser = !dispatch(isRoleLevel(UserRoleLevel.TESSARA_SUPER_USER));
    const isTessaraProtocol = lastOpenedProtocol?.isTessaraProtocol;

    // not permitted to save protocol
    if ((!protocolEditorState.autosave && !forceSave) || (isTessaraRegularUser && isTessaraProtocol) || isOperator) {
      return null;
    }

    // does not currently opened any protocol
    if (!lastOpenedProtocol?.id) return null;

    if (lastOpenedProtocol?.id !== NEW_PROTOCOL_ID) {
      response = await dispatch(updateProtocolState(lastOpenedProtocol.id));
      dispatch(setLastOpenedProtocol(response));
      dispatch(saveState());

      return response;
    }

    if (_.isEmpty(cardState.cards)) {
      let labwareFlag = false;

      Object.values(labwareState).forEach((item) => {
        if (item.IDPrefix) {
          labwareFlag = true;
        }
      });

      if (!labwareFlag) {
        return response;
      }
    }

    const protocol = await dispatch(getProtocolProperties(lastOpenedProtocol));

    response = await dispatch(
      invokeAPI(
        SignalRHub.PROTOCOL_MANAGEMENT,
        SignalREvent.CREATE_PROTOCOL,
        protocol,
        userId,
        lastOpenedProtocol?.trayType || trayType,
      ),
    );

    if (response.code === 0) {
      dispatch(setLastOpenedProtocol(response.payload));
      dispatch(setAvailableProtocols([...availableProtocols, response.payload]));
    }

    dispatch(saveState());

    return response;
  };

// protocols = [{ id, version }, ...]
export const deleteProtocols = (protocols) => async (dispatch, getState) => {
  const state = getState();
  const {sessionState} = state;
  const {userId} = sessionState.user;

  const response = await dispatch(
    invokeAPI(SignalRHub.PROTOCOL_MANAGEMENT, SignalREvent.DELETE_PROTOCOLS, protocols, userId),
  );

  return response.payload;
};

export const renameProtocol = (protocol, newName) => async (dispatch) => {
  const {id} = protocol;

  const response = await dispatch(updateProtocolState(id, {Name: newName}));
  return response;
};

export const renameProtocolFromProtocolEditor = (newName) => async (dispatch, getState) => {
  const state = getState();
  const {protocolState, protocolEditorState} = state;
  const {lastOpenedProtocol, lastProtocolCheckpoint} = protocolState;
  const {autosave} = protocolEditorState;

  let newProtocolData = {...lastOpenedProtocol, name: newName};

  if (!autosave) {
    const {openedProtocol: protocolCheckpoint} = lastProtocolCheckpoint || {};

    dispatch(setProtocolCheckpoint({...protocolCheckpoint, name: newName}, false));
  }

  if (lastOpenedProtocol?.id && lastOpenedProtocol?.id !== '-1') {
    newProtocolData = await dispatch(renameProtocol(lastOpenedProtocol, newName));
  }

  dispatch(setLastOpenedProtocol(newProtocolData));
  await dispatch(saveState(false));

  return newProtocolData;
};

export const downloadProtocol =
  (protocolIds = [], folderIds = []) =>
  async (dispatch, getState) => {
    const state = getState();
    const {sessionState, configState} = state;
    const {userId} = sessionState.user;

    const response = await callAPI(
      'POST',
      configState.hubHost.urlAuthoringLocation,
      `api/ProtocolManagement/${ApiRouteMap.DOWNLOAD_PROTOCOL}`,
      {protocolIds, folderIds, userId},
      null,
      'blob',
    );

    return response;
  };

export const uploadProtocol = (csvFiles, protocolMovingConflictOptions) => async (dispatch, getState) => {
  const state = getState();
  const {sessionState, configState, protocolExplorerState} = state;
  const {userId} = sessionState.user;
  const {currentProtocolFolder} = protocolExplorerState;

  const response = await callAPI(
    'POST',
    configState.hubHost.urlAuthoringLocation,
    `api/ProtocolManagement/${ApiRouteMap.UPLOAD_PROTOCOL}`,
    {
      csvFiles,
      userId,
      protocolMovingConflictOptions,
      FolderId: currentProtocolFolder?.id || null,
    },
  );

  return response;
};

export const cancelUploadProtocol = async () => {
  const {dispatch} = GlobalReduxStore.getStore();
  cancelAxiosRequest('canceled upload protocol by user');
  dispatch(hideLoading());
  dispatch(
    showSnackbar(
      Feedback.ERROR,
      intl.formatMessage({
        id: 'explorer.explorerMenuBarContainer.uploadProtocol.request.cancel',
      }),
    ),
  );
};

export const uploadProtocolZip = (base64Files) => async (dispatch, getState) => {
  const state = getState();
  const {sessionState, configState, protocolExplorerState} = state;
  const {userId} = sessionState.user;
  const {currentProtocolFolder} = protocolExplorerState;

  const response = await callAPI(
    'POST',
    configState.hubHost.urlAuthoringLocation,
    `api/ProtocolManagement/${ApiRouteMap.UPLOAD_PROTOCOL_ZIP}`,
    {
      Base64Files: base64Files,
      UserId: userId,
      FolderId: currentProtocolFolder?.id || null,
    },
    null,
    null,
    null,
  );

  return response;
};

export const confirmUploadProtocolZip = (uploadKey, mappedLabwareIDs) => async (dispatch, getState) => {
  const state = getState();
  const {sessionState, configState, protocolExplorerState} = state;
  const {userId} = sessionState.user;
  const {currentProtocolFolder} = protocolExplorerState;

  const response = await callAPI(
    'POST',
    configState.hubHost.urlAuthoringLocation,
    `api/ProtocolManagement/${ApiRouteMap.CONFIRM_UPLOAD_PROTOCOL_ZIP}`,
    {
      UploadKey: uploadKey,
      UserId: userId,
      FolderId: currentProtocolFolder?.id || null,
      MappedLabwareIDs: mappedLabwareIDs,
    },
  );

  return response;
};

export const duplicateProtocols = (protocolIds) => async (dispatch, getState) => {
  const state = getState();
  const {sessionState} = state;
  const {userId} = sessionState.user;

  const response = await dispatch(
    invokeAPI(SignalRHub.PROTOCOL_MANAGEMENT, SignalREvent.DUPLICATE_PROTOCOLS, protocolIds, userId),
  );

  return response;
};

export const sendProtocol = (protocolIds, targetUserIds) => async (dispatch, getState) => {
  const state = getState();
  const {sessionState} = state;
  const {userId} = sessionState.user;

  const response = await dispatch(
    invokeAPI(SignalRHub.PROTOCOL_MANAGEMENT, SignalREvent.SEND_PROTOCOL, protocolIds, userId, targetUserIds),
  );

  return response;
};

export const sendProtocolsAndFoldersToUsers = (protocolIds, folderIds, targetUserIds) => async (dispatch, getState) => {
  const state = getState();
  const {sessionState} = state;
  const {userId} = sessionState.user;

  const response = await sendProtcolsAndFolder(protocolIds, folderIds, userId, targetUserIds);
  dispatch(hideLoading());
  if (
    response.code === ServiceResponseCode.UNAUTH_ACTION_BECAUSE_NOT_TESSARA ||
    response.code === ServiceResponseCode.UNAUTH_FOLDER_ACTION_BECAUSE_NOT_TESSARA
  ) {
    dispatch(showSnackbar(Feedback.ERROR, intl.formatMessage({id: 'explorer.download.error.tessaraInsideFolder'})));
    return;
  }

  if (response.code !== ServiceResponseCode.OK) {
    return;
  }
  let contentType = '';
  if (protocolIds.length > 0 && folderIds.length > 0) {
    contentType = 'Item';
  } else if (protocolIds.length > 0) {
    contentType = 'Protocol';
  } else if (folderIds.length > 0) {
    contentType = 'Folder';
  }

  const snackbarMessage = intl.formatMessage(
    {id: 'explorer.explorerMenuBarContainer.sendProtocolModal.sendSuccessMessage'},
    {
      itemCount: [...protocolIds, ...folderIds].length,
      userCount: targetUserIds.length,
      contentType,
    },
  );

  dispatch(showSnackbar(Feedback.SUCCESS, snackbarMessage));
};

export const shareUnshareProtocolsAndFolders =
  (protocolIds, folderIds, targetUserIds, unshareTargetUserIds) => async (dispatch, getState) => {
    const state = getState();
    const {sessionState} = state;
    const {userId} = sessionState.user;
    const sharedProtocolIds = targetUserIds.length > 0 ? protocolIds : [];
    const unsharedProtocolIds = unshareTargetUserIds.length > 0 ? protocolIds : [];
    const sharedFolderIds = targetUserIds.length > 0 ? folderIds : [];
    const unsharedFolderIds = unshareTargetUserIds.length > 0 ? folderIds : [];

    const response = await shareUnshareProtocolAndFolderApi(
      sharedProtocolIds,
      unsharedProtocolIds,
      sharedFolderIds,
      unsharedFolderIds,
      userId,
      targetUserIds,
      unshareTargetUserIds,
    );
    dispatch(hideLoading());

    if (response.code !== 0) {
      return null;
    }

    const snackbarMessage = intl.formatMessage({
      id: 'explorer.explorerMenuBarContainer.shareProtocolAndFolder.updateMessage',
    });

    dispatch(showSnackbar(Feedback.SUCCESS, snackbarMessage));
    return response;
  };

export const saveRunningProtocol = (protocol) => async (dispatch, getState) => {
  const state = getState();
  const {protocolState, sessionState, protocolExplorerState, protocolEditorState} = state;
  const {autosave} = protocolEditorState;
  const {lastOpenedProtocol} = protocolState;
  const {user} = sessionState;
  const {protocolFolderList} = protocolExplorerState;
  let runningProtocol = protocol ?? lastOpenedProtocol;

  runningProtocol.user = user;

  if (!autosave) {
    runningProtocol = {
      ...runningProtocol,
      ...(await dispatch(getProtocolProperties(runningProtocol))),
    };
  }

  const response = await dispatch(
    invokeAPI(SignalRHub.STATE, SignalREvent.SAVE_RUNNING_PROTOCOL, runningProtocol, user, protocolFolderList),
  );

  return response;
};

export const clearRunningProtocol = () => async (dispatch) => {
  const response = await dispatch(invokeAPI(SignalRHub.STATE, SignalREvent.CLEAR_RUNNING_PROTOCOL));

  return response;
};

export const getRunningProtocol = () => async (dispatch) => {
  const response = await dispatch(invokeAPI(SignalRHub.STATE, SignalREvent.GET_RUNNING_PROTOCOL));

  return response;
};

export const updateRunningProtocol = () => async (dispatch, getState) => {
  const {runtimeState, protocolEditorState} = getState();
  const {id} = runtimeState;

  dispatch(showLoading());
  const csv = dispatch(convertCardStateToCsv());

  if (id !== '-1') {
    let updatedProtocol = {};
    if (protocolEditorState.autosave) {
      updatedProtocol = await dispatch(updateProtocolState(id));
      dispatch(saveState());
    }

    await dispatch(saveRunningProtocol(updatedProtocol));
  }

  await dispatch(invokeAPI(SignalRHub.INSTRUMENT, SignalREvent.UPDATE_CURRENT_RUN_PROTOCOL, csv));
  dispatch(hideLoading());
};

export const onExistProtocolRunning = () => async (dispatch, getState) => {
  const response = await dispatch(getRunningProtocol());
  const runningProtocolId = response?.payload?.id;
  const {protocolState, protocolEditorState} = getState();

  const {autosave} = protocolEditorState;
  const {lastOpenedProtocol} = protocolState;
  const lastOpenedProtocolId = lastOpenedProtocol?.id;
  const isOperator = dispatch(isUserOperator());

  const isLastOpenedProtocolNewUnsavedProtocol = lastOpenedProtocolId === NEW_PROTOCOL_ID && !autosave;

  if (
    lastOpenedProtocolId === runningProtocolId &&
    runningProtocolId &&
    (!isLastOpenedProtocolNewUnsavedProtocol || autosave)
  ) {
    // if not an unsaved new protocol
    if (!isOperator) dispatch(newProtocol());
    return true;
  }

  return false;
};

export const updateLabwareCurrentRunProtocol = (labwareState) => async (dispatch, getState) => {
  const state = getState();
  const runningProtocolResponse = await dispatch(getRunningProtocol());

  const {sessionState} = state;
  const {userId} = sessionState.user;

  const protocol = runningProtocolResponse?.payload ?? {
    id: undefined,
    version: undefined,
    name: undefined,
  };
  const {id, version} = protocol;

  const response = await dispatch(
    invokeAPI(SignalRHub.PROTOCOL_MANAGEMENT, SignalREvent.UPDATE_PROTOCOL, id, version, userId, {
      CsvContent: dispatch(convertCardStateToCsv(labwareState)),
    }),
  );

  return response.payload;
};

// eslint-disable-next-line default-param-last
export function getMinTipPosition(maxTipPosition, useMinFromConfig = false, deckNumber, labwareHeight) {
  return (dispatch, getState) => {
    const {configState, protocolState} = getState();
    const {
      allowedTipDepth: allowedTipDepthConfig,
      highVolumeAllowedTipDepth: highVolumeAllowedTipDepthConfig,
      minTipPosition: configMinTipPosition,
      highVolumeMinTipZatDeck5,
      defaultTipPosition,
    } = configState;

    const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;

    const allowedTipDepth =
      (protocolTrayType === TrayType.HIGH_VOLUME_CADDY ? highVolumeAllowedTipDepthConfig : allowedTipDepthConfig) ?? 0;
    let minTipPosition = maxTipPosition - allowedTipDepth;

    if (protocolTrayType === TrayType.HIGH_VOLUME_CADDY && Number(deckNumber) === 5) {
      minTipPosition = highVolumeMinTipZatDeck5 - (labwareHeight - maxTipPosition);
    }

    let allowedMinTipPosition = useMinFromConfig ? configMinTipPosition : defaultTipPosition;

    const parsedMaxTipPosition = parseFloat((Math.floor(maxTipPosition * 100) / 100).toFixed(2));
    allowedMinTipPosition = Math.min(parsedMaxTipPosition, allowedMinTipPosition);

    const tipPosition = minTipPosition < allowedMinTipPosition ? allowedMinTipPosition : minTipPosition;

    return parseFloat((Math.ceil(tipPosition * 100) / 100).toFixed(2));
  };
}

export function getMinTipPositionByLabwareId(labwareId, useMinFromConfig = false, indexLabware = 0) {
  return (dispatch, getState) => {
    const {labwareState} = getState();

    let selectedLabware = find(labwareState, {LabwareID: labwareId});
    const deckNumber = Object.keys(labwareState).find(
      (key) => labwareState[key]?.LabwareID === selectedLabware?.LabwareID,
    );

    if (indexLabware && selectedLabware?.labwareIndexes) {
      selectedLabware = selectedLabware?.labwareIndexes[indexLabware];
    }
    const maxTipPosition = selectedLabware?.wellDepth ?? 0;
    const labwareHeight = selectedLabware?.height ?? 0;

    return dispatch(getMinTipPosition(maxTipPosition, useMinFromConfig, deckNumber, labwareHeight));
  };
}

export function getMinTipPositionByLabwarePrefixId(labwarePrefixId, deckNumber) {
  return (dispatch, getState) => {
    const {availableLabwareState} = getState();

    const selectedLabware = find(availableLabwareState, {IDPrefix: labwarePrefixId});

    const maxTipPosition = selectedLabware?.wellDepth ?? 0;
    const labwareHeight = selectedLabware?.height ?? 0;

    return dispatch(getMinTipPosition(maxTipPosition, false, deckNumber, labwareHeight));
  };
}

export function getMaxTipPositionByLabwareId(labwareId) {
  return (dispatch, getState) => {
    const {labwareState} = getState();

    const selectedLabware = find(labwareState, {LabwareID: labwareId});
    const maxTipPosition = selectedLabware?.wellDepth ?? 0;

    return parseFloat((Math.floor(maxTipPosition * 100) / 100).toFixed(2));
  };
}

export const validateProtocolBeforeRun = () => (dispatch, getState) => {
  const state = getState();
  const {cardState} = state;
  const {tasks} = cardState;

  const newTasks = jsonCopy(tasks);

  Object.keys(newTasks).forEach((e) => {
    const task = newTasks[e];
    const {labwareID, tipPosition, mixingPositionAspirate, mixingPositionDispense, type} = task;

    const minTipPosition = dispatch(getMinTipPositionByLabwareId(labwareID, true));
    const defaultMinTipPosition = dispatch(getMinTipPositionByLabwareId(labwareID));
    const maxTipPosition = dispatch(getMaxTipPositionByLabwareId(labwareID));

    if (type === TaskType.ASPIRATE || type === TaskType.DISPENSE) {
      if (minTipPosition > tipPosition) {
        newTasks[e].tipPosition = minTipPosition;
      }

      if (maxTipPosition < tipPosition) {
        newTasks[e].tipPosition = defaultMinTipPosition;
      }
    }

    if (type === TaskType.MIXING || type === TaskType.ASPIRATE || type === TaskType.DISPENSE) {
      if (minTipPosition > mixingPositionAspirate) {
        newTasks[e].mixingPositionAspirate = minTipPosition;
      }

      if (minTipPosition > mixingPositionDispense) {
        newTasks[e].mixingPositionDispense = minTipPosition;
      }

      if (maxTipPosition < mixingPositionAspirate) {
        newTasks[e].mixingPositionAspirate = defaultMinTipPosition;
      }

      if (maxTipPosition < mixingPositionDispense) {
        newTasks[e].mixingPositionDispense = defaultMinTipPosition;
      }
    }
  });

  dispatch(restoreCardTaskState(newTasks));
};

export const createProtocolFolder = (folderName) => async (dispatch, getState) => {
  const state = getState();
  const {sessionState, protocolExplorerState} = state;
  const {userId} = sessionState.user;

  const {currentProtocolFolder} = protocolExplorerState;
  const currentProtocolFolderId = currentProtocolFolder?.id || null;

  const response = await dispatch(
    invokeAPI(
      SignalRHub.PROTOCOL_MANAGEMENT,
      SignalREvent.CREATE_PROTOCOL_FOLDER,
      folderName,
      currentProtocolFolderId,
      userId,
    ),
  );

  return response;
};

export const renameProtocolFolder = (folder, newName) => async (dispatch, getState) => {
  const state = getState();
  const {sessionState} = state;
  const {userId} = sessionState.user;
  const {id} = folder;

  const response = await dispatch(
    invokeAPI(SignalRHub.PROTOCOL_MANAGEMENT, SignalREvent.RENAME_PROTOCOL_FOLDER, id, userId, newName),
  );

  return response.payload;
};

export const deleteProtocolsAndFolders = (protocols, folders) => async (dispatch, getState) => {
  const state = getState();
  const {sessionState} = state;
  const {userId} = sessionState.user;

  const response = await dispatch(
    invokeAPI(SignalRHub.PROTOCOL_MANAGEMENT, SignalREvent.DELETE_PROTOCOLS_AND_FOLDERS, protocols, folders, userId),
  );

  return response;
};

export const duplicateProtocolsAndFolders = (protocols, folders, favoriteProtocolIds) => async (dispatch, getState) => {
  const state = getState();
  const {sessionState} = state;
  const {userId} = sessionState.user;

  const response = await dispatch(
    invokeAPI(
      SignalRHub.PROTOCOL_MANAGEMENT,
      SignalREvent.DUPLICATE_PROTOCOLS_AND_FOLDERS,
      protocols,
      folders,
      userId,
      favoriteProtocolIds,
    ),
  );

  return response;
};

export const checkTessaraProtocolDroppedInSharedFolder = async (protocols, targetFolderId) => {
  const isTessaraProtocol = protocols.some((protocol) => protocol?.isTessaraProtocol);

  if (!isTessaraProtocol) return false;

  const {payload: targetFolder, code: responseCode} = await getFolderDetailsById(targetFolderId);

  if (responseCode !== ServiceResponseCode.OK) return false;

  const isTargetFolderShared = targetFolder?.sharedTo?.length > 0;

  if (!isTargetFolderShared) return false;

  return true;
};

export const moveProtocolsAndFolders =
  (
    // eslint-disable-next-line default-param-last
    protocols = [],
    // eslint-disable-next-line default-param-last
    folders = [],
    targetFolderId,
  ) =>
  async (dispatch, getState) => {
    const state = getState();
    const {sessionState, protocolState} = state;
    const {userId} = sessionState.user;
    const {lastOpenedProtocol} = protocolState || {};

    const doMoveProtocolsAndFolders = async () => {
      dispatch(showLoading(intl.formatMessage({id: 'explorer.moveProtocols.loadingMessage'})));

      const protocolIds = protocols?.map((p) => p.id) || [];
      const folderIds = folders?.map((f) => f.id) || [];

      const response = await dispatch(
        invokeAPI(
          SignalRHub.PROTOCOL_MANAGEMENT,
          SignalREvent.MOVE_PROTOCOLS_AND_FOLDERS,
          protocolIds,
          folderIds,
          targetFolderId,
          userId,
        ),
      );

      if (lastOpenedProtocol && protocolIds.includes(lastOpenedProtocol.id)) {
        dispatch(setLastOpenedProtocol({...lastOpenedProtocol, folderId: targetFolderId}));
      }

      dispatch(hideLoading());

      return response;
    };

    if (folders.length) {
      const showModal = () => (doDispatch) =>
        new Promise((resolve) => {
          const callback = async (isConfirm) => {
            if (isConfirm) {
              const res = await doMoveProtocolsAndFolders();
              resolve(res);
            } else {
              resolve(null);
            }
          };
          doDispatch(showSameFolderNameDetectedModal(callback));
        });

      // FIXME: Too smart to be handled in Frontend! Validation of conflicted folders/protocols can be handle in backend
      const foldersAndProtocolsInTargetFolder = await dispatch(fetchProtocolsAndFoldersByFolderId(targetFolderId));
      const {protocolFolders} = foldersAndProtocolsInTargetFolder;

      const isSameNameFolder = protocolFolders.some((folder) =>
        folders.map((e) => e.name?.toLowerCase()).includes(folder.name?.toLowerCase()),
      );

      const isSameSourceAndTargetId = folders.some((folder) => folder?.parentId === targetFolderId);

      if (isSameNameFolder && !isSameSourceAndTargetId) {
        dispatch(hideLoading());
        return dispatch(showModal());
      }
    }

    return doMoveProtocolsAndFolders();
  };

export const copyProtocolsAndFolders =
  (
    // eslint-disable-next-line default-param-last
    protocols = [],
    // eslint-disable-next-line default-param-last
    folders = [],
    targetFolderId,
  ) =>
  async (dispatch, getState) => {
    const state = getState();
    const {sessionState} = state;
    const {userId} = sessionState.user;

    const protocolIds = protocols?.map((p) => p.id) || [];
    const folderIds = folders?.map((f) => f.id) || [];

    const response = await dispatch(
      invokeAPI(
        SignalRHub.PROTOCOL_MANAGEMENT,
        SignalREvent.COPY_PROTOCOLS_AND_FOLDERS,
        protocolIds,
        folderIds,
        targetFolderId,
        userId,
      ),
    );

    return response;
  };

export const cutProtocolsAndFoldersToClipboard =
  (protocols = [], folders = []) =>
  (dispatch) => {
    const items = _.cloneDeep([...protocols, ...folders]);

    batch(() => {
      dispatch(setProtocolClipboardEventType(ClipboardEventType.CUT));
      dispatch(setProtocolClipboardItems(items));
    });
  };

export const copyProtocolsAndFoldersToClipboard =
  (protocols = [], folders = []) =>
  (dispatch) => {
    const items = _.cloneDeep([...protocols, ...folders]);

    batch(() => {
      dispatch(setProtocolClipboardEventType(ClipboardEventType.COPY));
      dispatch(setProtocolClipboardItems(items));
    });
  };

export const pasteProtocolsAndFoldersFromClipboard = (targetFolderId) => async (dispatch, getState) => {
  const state = getState();
  const {protocolExplorerState} = state;
  const {protocolClipboardEventType, protocolClipboardItems} = protocolExplorerState;

  const {protocols, folders} = getDataProtocolsAndFolders(protocolClipboardItems);

  let response;

  switch (protocolClipboardEventType) {
    case ClipboardEventType.CUT:
      response = await dispatch(moveProtocolsAndFolders(protocols, folders, targetFolderId));
      break;
    case ClipboardEventType.COPY:
      response = await dispatch(copyProtocolsAndFolders(protocols, folders, targetFolderId));
      break;
    default:
      break;
  }

  dispatch(resetProtocolClipboard());

  return response;
};

const replaceNewUnsavedRunningProtocol = (protocolId, protocolToReplace) => async (dispatch) => {
  const runningProtocol = await dispatch(getRunningProtocol());
  const {payload: runningProtocolData} = runningProtocol;

  if (protocolId === runningProtocolData?.id && protocolId === '-1') {
    await dispatch(saveRunningProtocol(protocolToReplace));
  }
};

export const updateCurrentRunningProtocol = (runningProtocol) => async (dispatch) => {
  if (runningProtocol) {
    dispatch(setProtocolStateRunningProtocol(runningProtocol));
  } else {
    dispatch(setProtocolStateRunningProtocol(null));
  }
};

export const clearAndUpdateCurrentRunningProtocol = () => (dispatch) => {
  batch(() => {
    dispatch(clearInstrumentProcess(true));
    dispatch(clearRunningProtocol());
    dispatch(setProtocolStateRunningProtocol(null));
  });
};

export const isFolderUpdated = (allProtocolList, newProtocolFolderList) => async (dispatch, getState) => {
  const state = getState();
  const {
    protocolState: {availableProtocols},
    protocolExplorerState: {protocolFolderList, currentProtocolFolder},
    sessionState: {user},
  } = state;
  const isOperator = await dispatch(isUserOperator());
  const isUserOwnFolder =
    !currentProtocolFolder || protocolFolderList[protocolFolderList.length - 1]?.owner === user.userId;
  if (isUserOwnFolder && !isOperator) return false;
  const isFolderListNotChanged =
    newProtocolFolderList.length > 0 &&
    protocolFolderList.length > 0 &&
    newProtocolFolderList?.[newProtocolFolderList.length - 1]?.id ===
      protocolFolderList?.[protocolFolderList.length - 1]?.id;

  if (isFolderListNotChanged) {
    const newProtocolListIds = allProtocolList.map((item) => item.ID);
    const protocolListIds = availableProtocols.map((item) => item.ID);
    const protocolListChanged =
      newProtocolListIds.length !== protocolListIds.length ||
      newProtocolListIds.some((item) => !protocolListIds.includes(item)).length > 0;
    return protocolListChanged;
  }
  return false;
};

export const isProtocolExists = (protocolToCheck, callback, latestProtocolData) => async (dispatch, getState) => {
  const {user} = getState().sessionState;
  const {id: protocolId, isTessaraProtocol} = protocolToCheck || {};

  const updatedProtocol = latestProtocolData || (await getProtocolByIdAndUserId(protocolId, user?.userId));
  const hasTessaraProtocolBeenConverted = isTessaraProtocol && !updatedProtocol?.payload?.isTessaraProtocol;

  if (updatedProtocol?.code === ServiceResponseCode.OK && !hasTessaraProtocolBeenConverted) return true;

  dispatch(
    showProtocolOrFolderNotAvailable(async () => {
      await dispatch(setCurrentProtocolFolder(null));
      dispatch(changeView(ViewMode.EXPLORER));
      if (callback) callback();
    }),
  );

  return false;
};

export const isFolderContainsTessaraProtocol = (folderIds, userId, callback) => async (dispatch) => {
  const response = await isFolderContainsTessaraProtocolApi(folderIds, userId);

  if (response.payload) {
    dispatch(showFolderHasTessaraProtocol(callback));
    return true;
  }
  return false;
};
